/**
 * @fileoverview Flag icon component.
 * @summary SVG flag icons for language selector.
 *
 * @package KaraoNow
 * @module apps/front/src/components/FlagIcon
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import type { ReactElement } from 'react'

interface FlagIconProps {
  code: string
  className?: string
}

/**
 * @summary Flag icon component using SVG.
 * @param {FlagIconProps} props - Component props.
 * @returns {React.ReactElement} Flag icon component.
 */
export default function FlagIcon({ code, className = '' }: FlagIconProps) {
  const flags: Record<string, ReactElement> = {
    fr: (
      <svg viewBox="0 0 640 480" className={className}>
        <g fillRule="evenodd" strokeWidth="1pt">
          <path fill="#fff" d="M0 0h640v480H0z" />
          <path fill="#002654" d="M0 0h213.3v480H0z" />
          <path fill="#ce1126" d="M426.7 0H640v480H426.7z" />
        </g>
      </svg>
    ),
    en: (
      <svg viewBox="0 0 50 30" className={className}>
        <defs>
          <clipPath id="uk-clip-en">
            <path d="M25,15h25v15zv15h-25zh-25v-15zv-15h25z" />
          </clipPath>
        </defs>
        <path d="M0,0v30h50v-30z" fill="#012169" />
        <path d="M0,0 50,30M50,0 0,30" stroke="#fff" strokeWidth="6" />
        <path d="M0,0 50,30M50,0 0,30" clipPath="url(#uk-clip-en)" stroke="#C8102E" strokeWidth="4" />
        <path d="M-1 11h22v-12h8v12h22v8h-22v12h-8v-12h-22z" fill="#C8102E" stroke="#FFF" strokeWidth="2" />
      </svg>
    ),
    es: (
      <svg viewBox="0 0 640 480" className={className}>
        <path fill="#aa151b" d="M0 0h640v160H0z" />
        <path fill="#f1bf00" d="M0 160h640v160H0z" />
        <path fill="#aa151b" d="M0 320h640v160H0z" />
      </svg>
    ),
    it: (
      <svg viewBox="0 0 640 480" className={className}>
        <g fillRule="evenodd" strokeWidth="1pt">
          <path fill="#fff" d="M0 0h640v480H0z" />
          <path fill="#009246" d="M0 0h213.3v480H0z" />
          <path fill="#ce2b37" d="M426.7 0H640v480H426.7z" />
        </g>
      </svg>
    ),
    pt: (
      <svg viewBox="0 0 600 400" className={className}>
        <rect width="600" height="400" fill="#ed1c24" />
        <rect width="240" height="400" fill="#006035" />
        <g transform="translate(300, 200)">
          <circle r="70" fill="#fff200" stroke="#231f20" strokeWidth="2" />
          <circle r="55" fill="#006035" />
          <g fill="#fff200">
            <ellipse cx="0" cy="-18" rx="10" ry="14" />
            <ellipse cx="0" cy="18" rx="10" ry="14" />
            <ellipse cx="-18" cy="0" rx="14" ry="10" />
            <ellipse cx="18" cy="0" rx="14" ry="10" />
            <ellipse cx="-12" cy="-12" rx="7" ry="7" />
            <ellipse cx="12" cy="-12" rx="7" ry="7" />
            <ellipse cx="-12" cy="12" rx="7" ry="7" />
            <ellipse cx="12" cy="12" rx="7" ry="7" />
          </g>
          <rect x="-22" y="-28" width="44" height="56" fill="#ed1c24" rx="3" />
          <rect x="-17" y="-23" width="34" height="46" fill="#006035" rx="2" />
          <path
            d="M-12 -18 L0 -6 L12 -18 L6 -6 L12 6 L0 0 L-12 6 L-6 -6 Z"
            fill="#fff200"
            stroke="#231f20"
            strokeWidth="0.5"
          />
        </g>
      </svg>
    ),
    ja: (
      <svg viewBox="0 0 640 480" className={className}>
        <rect width="640" height="480" fill="#fff" stroke="#000" strokeWidth="9" />
        <circle cx="320" cy="240" r="96" fill="#bc002d" stroke="#fff" strokeWidth="3" />
      </svg>
    ),
    zh: (
      <svg viewBox="0 0 640 480" className={className}>
        <path fill="#de2910" d="M0 0h640v480H0z" />
        <g fill="#ffde00">
          <path d="M128 96h64v32h-64z" />
          <path d="M192 96h64v32h-64z" />
          <path d="M256 96h64v32h-64z" />
          <path d="M128 128h64v32h-64z" />
          <path d="M192 128h64v32h-64z" />
          <path d="M256 128h64v32h-64z" />
          <path d="M128 160h64v32h-64z" />
          <path d="M192 160h64v32h-64z" />
          <path d="M256 160h64v32h-64z" />
          <circle cx="320" cy="240" r="80" fill="#ffde00" />
        </g>
        <circle cx="320" cy="240" r="48" fill="#de2910" />
      </svg>
    ),
    ko: (
      <svg viewBox="0 0 900 600" className={className}>
        <rect width="900" height="600" fill="#fff" />
        {/* Taeguk (Yin-Yang symbol) - centered */}
        <g transform="translate(450 300)">
          {/* Upper blue half circle */}
          <path
            fill="#0047a0"
            d="M0,-120 C-66.27,-120 -120,-66.27 -120,0 C-120,66.27 -66.27,120 0,120 L0,0 Z"
          />
          {/* Lower red half circle */}
          <path
            fill="#cd2e3a"
            d="M0,0 C0,-66.27 53.73,-120 120,-120 C186.27,-120 240,-66.27 240,0 C240,66.27 186.27,120 120,120 C53.73,120 0,66.27 0,0 Z"
          />
          {/* Blue dot */}
          <circle cx="0" cy="-60" r="40" fill="#0047a0" />
          {/* Red dot */}
          <circle cx="0" cy="60" r="40" fill="#cd2e3a" />
        </g>
        {/* Trigram: Heaven (top-left corner) - three solid horizontal lines */}
        <g transform="translate(150 100)">
          <rect x="0" y="0" width="90" height="12" fill="#000" />
          <rect x="0" y="28" width="90" height="12" fill="#000" />
          <rect x="0" y="56" width="90" height="12" fill="#000" />
        </g>
        {/* Trigram: Water (top-right corner) - solid, broken, solid */}
        <g transform="translate(660 100)">
          <rect x="0" y="0" width="90" height="12" fill="#000" />
          <rect x="0" y="28" width="35" height="12" fill="#000" />
          <rect x="55" y="28" width="35" height="12" fill="#000" />
          <rect x="0" y="56" width="90" height="12" fill="#000" />
        </g>
        {/* Trigram: Fire (bottom-left corner) - solid, solid, broken */}
        <g transform="translate(150 488)">
          <rect x="0" y="0" width="90" height="12" fill="#000" />
          <rect x="0" y="28" width="90" height="12" fill="#000" />
          <rect x="0" y="56" width="35" height="12" fill="#000" />
          <rect x="55" y="56" width="35" height="12" fill="#000" />
        </g>
        {/* Trigram: Earth (bottom-right corner) - broken, solid, broken */}
        <g transform="translate(660 488)">
          <rect x="0" y="0" width="35" height="12" fill="#000" />
          <rect x="55" y="0" width="35" height="12" fill="#000" />
          <rect x="0" y="28" width="90" height="12" fill="#000" />
          <rect x="0" y="56" width="35" height="12" fill="#000" />
          <rect x="55" y="56" width="35" height="12" fill="#000" />
        </g>
      </svg>
    ),
  }

  return flags[code] || <span className={className}>{code.toUpperCase()}</span>
}
